import type { FieldEditorValues } from './config';
import type { FieldSummary } from './constants';

export interface PreviewValidationContext {
  fields: Record<string, FieldSummary>;
  fieldConfigs: Record<string, FieldEditorValues>;
  containers: Record<string, string[]>;
  previewData: Record<string, unknown>;
  hiddenFieldIds: ReadonlySet<string>;
  hiddenSectionIds: ReadonlySet<string>;
}

export const isValidUrl = (value: string): boolean => {
  try {
    const url = new URL(value);

    return Boolean(url);
  } catch {
    return false;
  }
};

export const isValueEmpty = (value: unknown): boolean => {
  if (value == null || value === '') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};

/**
 * Validates the previewed form, returning a map of fieldId -> error message for
 * every visible, editable field that fails. Skips read-only, hidden, and
 * conditionally-hidden fields (and fields in hidden sections), so it honours the
 * same visibility the preview renders with.
 *
 * Used by the standalone `FormPreview` (Storybook / no react-hook-form context),
 * which validates against local editor state. The live `LiveFormPreview` instead
 * delegates to the form's own `trigger()`, since it renders against a real
 * react-hook-form context with a resolver.
 */
export const collectValidationErrors = (
  containerOrder: string[],
  ctx: PreviewValidationContext
): Record<string, string> => {
  const errors: Record<string, string> = {};

  for (const sectionId of containerOrder) {
    if (ctx.hiddenSectionIds.has(sectionId)) {
      continue;
    }

    const fieldIds = ctx.containers[sectionId] ?? [];
    for (const fieldId of fieldIds) {
      const field = ctx.fields[fieldId];
      if (!field) {
        continue;
      }
      // Skip read-only and hidden fields. `hidden` is checked on both carriers
      // for the same reason `readOnly` is read top-level: a field with no
      // `config` entry still carries the flag on its summary (CUS-321).
      if (field.readOnly || field.hidden || ctx.fieldConfigs[fieldId]?.hidden) {
        continue;
      }
      if (ctx.hiddenFieldIds.has(fieldId)) {
        continue;
      }

      const value = ctx.previewData[fieldId];
      if (field.required && isValueEmpty(value)) {
        errors[fieldId] = 'This field is required';
      } else if (
        field.type === 'url' &&
        !isValueEmpty(value) &&
        !isValidUrl(String(value))
      ) {
        errors[fieldId] = 'Please enter a valid URL';
      }
    }
  }

  return errors;
};
